import { monthDates, monthHeading } from '../diary-heading'
import { closedDiaryDay, visitColour } from '../day-state'
import { beginDiaryColours, colourDay, diaryColourKey, stateMark } from '../diary-colours'
import { diaryMoreMenu, diaryPage, finishDiary, countLine, diaryCounts, diaryEstimateLine, diaryDayTotals, diaryHeading, diaryMotion, diaryPanel, diaryOrderSettle, type DiaryOrderEffect, diaryMoveCapture, diaryMoveSettle, diarySelectionTint, diaryPreferenceKey, type DiaryMoveEffect } from '../diary-layout'
import { api, mutate } from '../api'
import { routeGen } from '../route-state'
import { currencySymbol } from '../round-config'
import { worksAlone } from '../me'
import { undoPatch } from '../undo'
import { timeIsAgreed } from '../rebook-message'
import { orderDay, autoScrollSpeed, pressOnControl, shouldGrab, AUTO_SCROLL_EDGE_PX, LONG_PRESS_MS } from '../day-order'
import { clear, dayLabel, el, loadError, todayISO, toast } from '../ui'
import {
  areaColours,
  areaOfClean,
  canMove,
  chipKind,
  dayMoney,
  monthDays,
  monthOf,
  monthRange,
  shiftMonth,
  shortMoney,
  visitWorker,
  type MonthDay,
} from '../month-view'
import type { Clean, Customer, SchedulePayload } from '../types'
import { nowWhose } from '../booking-days'
import { isSuggestion } from '../planning'
import { diaryCustomer, visitWords } from '../visit-words'
import { hasExtraJobs, loadRoundJobs, noteJobOneNames, severalJobsOn, visitJobLabel } from '../job-names'
import { diaryDayGroups, diaryDayOrder } from '../diary-stops'
import '../month.css'
import { lineDrawing, type Drawing } from '../picture-tiles'
import { tick } from '../haptic'
import { AREA_RENAMED, openAreaRename } from '../area-rename'

// The diary's Month view (Liam 30 Sep 2026; mockup docs/plans/month-view/mockup-v3.html, planned with Astra).
// The whole month: each day's jobs by name with the day's money, a total for each week, colours for the round's
// areas, and moving a job by dragging it (hold first on a phone) or tapping the day and Move. The rules for what
// shows on a day are the week diary's own (month-view.ts). The Week view stays the main diary: it opens on
// Week, and Month is one tap away, remembered on this phone.

// A day that has gone takes no booking and no move, in Month, Day and Week alike (bug hunt, 8 Oct 2026: the Diary let
// someone be placed on Tuesday two days back, and their message asked about that Tuesday).
export const GONE_DAY_WORDS = 'Pick today or a later day. A day that has gone cannot take a booking.'

export interface MonthHooks {
  // Back to the week diary, at the week holding this day when one is given.
  toWeek: (date?: string) => void
  isEditing?: () => boolean
  search?: () => string
  setSearch?: (value: string) => void
  dayExtras?: (date: string, rows: Clean[], payload: SchedulePayload) => {head: HTMLElement; footer: HTMLElement}
  book?: (date: string) => void
  renderVisit?: (clean: Clean, row: HTMLElement, payload: SchedulePayload, filtered: boolean) => void
  // The desktop New diary shows Day, Week and Month together: back to the week diary in that arrangement.
  toMode?: (mode: 'day' | 'week', date?: string) => void
  // "Message them" beside Undo after an agreed visit is moved (Release 2): opens that person in Messages.
  messageThem?: (clean: Clean) => { act: () => void; label: string }
  // A "Couldn't do it" visit moved: its sorry message, as Week's (taken before the move clears Couldn't do it).
  missedLink?: (clean: Clean) => ((date: string, version: number, before: { date: string; confirmed: number; time_agreed_for?: string | null }) => { act: () => void; label: string }) | null
  // The customer from the Diary's customers list, for the shared words (days offered in a first message).
  customerOf?: (id: number) => Customer | undefined
  // The phone's Needs you · Due · Pencilled tiles for the month on show (the redesign, phone 4: Month as Week).
  phoneTiles?: (payload: SchedulePayload, customers: Customer[] | null) => HTMLElement
  // The computer's Due column down the left and the month's dates taking a drop (Diary round 3).
  deskFrame?: (root: HTMLElement, payload: SchedulePayload, customers: Customer[] | null) => void
  // What that column needs beyond the month (this week, the enquiries, the jobs), read with it.
  deskLoad?: () => Promise<void>
  // The date Day or Week was showing: Month opens on its month (Astra's final check: switching keeps the date in view).
  startDate?: string
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

let first = monthOf(todayISO())
let payload: SchedulePayload | null = null
let customers: Customer[] | null = null
let picked: string | null = null
let landing: {id: number; date: string; reverse: boolean; move:DiaryMoveEffect} | null = null
let movingOpen: number | null = null
let workerFilter = ''
let areaFilter = ''
// A renamed area keeps its filter under its new name (area-rename.ts).
window.addEventListener(AREA_RENAMED, (e) => { const { from, to } = (e as CustomEvent<{ from: string; to: string }>).detail; if (areaFilter === from) areaFilter = to })
let dragging = false
let reloadAfterDrag = false
const expandedDays = new Set<string>()
let rowDrag: {
  row: HTMLElement; root: HTMLElement; cleanId: number; gen: number; pointerId: number; pointerType: string
  startX: number; startY: number; lastX: number; lastY: number; startedAt: number
  phase: 'press' | 'held'; timer: number | null; frame: number | null
  layer: HTMLElement | null; preview: HTMLElement | null; fingerDx: number; fingerDy: number
} | null = null
let orderLanding: DiaryOrderEffect | null = null
let monthStep = 0
let selectionEntry = false
let searchText = ''
// The phone's magnifier stays open across a redraw while it is in use, as Day and Week keep it.
let optionsOpen = false
// The computer's Find (magnifier) stays open across a redraw while it is in use, as Day and Week keep it.
let deskFindOpen = false
let sequence = 0
let hooks: MonthHooks = { toWeek: () => {} }
// Whether the month is the view on screen. Switching to the week, or any "Show me" that opens the week, turns it
// off, so a month answer arriving late never draws over the week (Astra, 30 Sep).
let active = false
// Visits with a move still saving: one move per visit at a time, as the week diary (Astra, 30 Sep).
const movesInFlight = new Set<number>()
// A new day picked in a Move box but not yet saved, by visit: a refresh must not throw it away (Astra, 30 Sep).
const moveDrafts = new Map<number, string>()


const phone = (): boolean => typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches
const touch = (): boolean => typeof matchMedia === 'function' && matchMedia('(hover: none)').matches
const firstName = (cl: Clean): string => (cl.customer_name ?? 'this visit').split(/\s+/)[0] ?? 'this visit'

export function mountMonth(h: MonthHooks): void {
  hooks = h
  active = true
  picked = null
  searchText = hooks.search?.() ?? searchText
  selectionEntry = false
  movingOpen = null
  expandedDays.clear()
  // Switched to from Day or Week: the month of the date they showed. Otherwise a month left in the past (opened last
  // month) comes back to this one; a month paged forward stays.
  if (h.startDate && !phone()) first = monthOf(h.startDate)
  else if (first < monthOf(todayISO())) first = monthOf(todayISO())
  void load()
}

export function monthOnDataChanged(order?: DiaryOrderEffect): void {
  if (!active) return
  if (order) orderLanding = order
  if (dragging || rowDrag) reloadAfterDrag = true
  else void load()
}

// The Due column's tabs beside the month: drawn again from what the month already has, with no new read.
export function monthRedraw(): void {
  if (!active || !payload || dragging || rowDrag) return
  render(document.querySelector<HTMLElement>('#screen')!)
}

// The week is taking over the screen: anything the month still has on its way is dropped.
export function leaveMonth(): void {
  active = false
  sequence++
  orderLanding = null; landing = null; monthStep = 0; selectionEntry = false
  endRowDrag()
  dragging = false
  reloadAfterDrag = false
}

// A read on coming back to the Diary (scheduleComeBack) is quiet: if it fails, the month already on screen stays as it
// is, with no error panel (Codex, 15 Oct 2026).
let quietNext = false
export function monthComeBack(): void {
  quietNext = true
  monthOnDataChanged()
}

async function load(): Promise<void> {
  const quiet = quietNext
  quietNext = false
  const seq = ++sequence
  const gen = routeGen
  const root = document.querySelector<HTMLElement>('#screen')!
  const range = monthRange(first)
  try {
    const [p, c] = await Promise.all([
      api<SchedulePayload>(`/api/schedule?from=${range.from}&to=${range.to}`),
      api<{ customers: Customer[] }>('/api/customers', 'customers').catch(() => null),
      // Several jobs on: each chip of a customer with more than one job names its job.
      loadRoundJobs(),
      // This week's read for Due: the computer's column waits for it; a phone draws the month first and fills its Due
      // tile when it arrives (Sonnet's check, 26 Oct 2026).
      !phone() ? hooks.deskLoad?.().catch(() => undefined) : undefined,
    ])
    if (c?.customers) noteJobOneNames(c.customers)
    if (gen !== routeGen || seq !== sequence || !active) return
    if (dragging || rowDrag) { reloadAfterDrag = true; return }
    if (hooks.isEditing?.()) return
    const detail = document.querySelector<HTMLDialogElement>('.diary-detail-dialog[open]')
    if (detail) { detail.addEventListener('close', () => { if (active) void load() }, {once:true}); return }
    payload = p
    customers = c?.customers ?? customers
    render(root)
    if (phone() && hooks.deskLoad && hooks.phoneTiles) {
      const tiles = hooks.phoneTiles
      void hooks.deskLoad().catch(() => undefined).then(() => {
        if (gen !== routeGen || seq !== sequence || !active || !payload) return
        root.querySelector('.diary-phone-tiles')?.replaceWith(tiles(payload, customers))
      })
    }
  } catch (err) {
    if (gen !== routeGen || seq !== sequence || !active) return
    if (dragging || rowDrag) { reloadAfterDrag = true; return }
    if (quiet && payload) return
    // The week diary keeps a copy on this phone for no signal; the month does not, so it says so and offers the week.
    clear(root)
    root.append(heading())
    loadError(root, `${(err as Error).message}. The month needs signal. The week view works without signal.`, () => void load())
    const back = el('button', { type: 'button', class: 'btn-primary' }, 'Open the week view')
    back.addEventListener('click', () => hooks.toWeek())
    root.append(back)
  }
}

function heading(): HTMLElement {
  return el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'The diary'), el('p', { class: 'muted' }, 'The whole month. Drag a job to move it. On a phone, hold it first. Or tap a day and use Move.')))
}

// Day · Week · Month (Liam, 8 Oct 2026: "we'll have a day, a week and a month and we'll have an easy toggle to go
// between them ... on brand"): one segmented control for the New diary, on a computer and a phone, in Day, Week and
// Month alike. The chosen one is lemon, like every Roundly switch.
// The Find button's magnifier, drawn as the Day and Week bar draws it.
function magnifier(): SVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '18'); svg.setAttribute('height', '18'); svg.setAttribute('aria-hidden', 'true')
  const ring = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
  ring.setAttribute('cx', '10.5'); ring.setAttribute('cy', '10.5'); ring.setAttribute('r', '6.5')
  const handle = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  handle.setAttribute('d', 'M15.5 15.5 20 20')
  for (const part of [ring, handle]) { part.setAttribute('fill', 'none'); part.setAttribute('stroke', 'currentColor'); part.setAttribute('stroke-width', '2'); part.setAttribute('stroke-linecap', 'round') }
  svg.append(ring, handle)
  return svg
}

// Switching (Liam, 9 Oct 2026: "should be satisfying to switch", then "could be more satisfying?"): the lemon slides
// to the view picked and settles with a small bounce (220ms), the page below slides in from the side tapped towards
// (Day to Week from the right, back from the left, 200ms), and the phone gives a light tap (haptic.ts). They confirm
// the choice (docs/ANIMATION-STANDARD.md); with reduced motion the lemon moves at once and nothing slides. The tap
// starts the lemon's slide; the screen is often redrawn before it ends, so the new switch carries on from there.
const VIEWS = ['day', 'week', 'month'] as const
const SLIDE_MS = 220
let slide: { from: number; to: number; at: number } | null = null
let entrance: { dir: number; at: number } | null = null
export function threeViewSwitch(current: 'day' | 'week' | 'month', pick: (view: 'day' | 'week' | 'month') => void): HTMLElement {
  const seg = el('div', { class: 'view-switch diary-views', role: 'group', 'aria-label': 'Diary view: day, week or month' })
  const thumb = el('span', { class: 'diary-views-thumb', 'aria-hidden': 'true' })
  const at = (i: number): void => { thumb.style.transform = `translateX(${i * 100}%)` }
  const index = VIEWS.indexOf(current)
  const elapsed = slide && slide.to === index ? Date.now() - slide.at : SLIDE_MS
  // Roughly how far the lemon has got after `elapsed` (it eases out fast, as 1 - (1 - t)³ does).
  const start = slide && elapsed < SLIDE_MS ? slide.from + (index - slide.from) * (1 - (1 - elapsed / SLIDE_MS) ** 3) : index
  slide = null
  at(start)
  if (start !== index) {
    if (typeof requestAnimationFrame === 'function') {
      thumb.style.transitionDuration = `${Math.max(80, Math.round(SLIDE_MS - elapsed))}ms`
      requestAnimationFrame(() => requestAnimationFrame(() => at(index)))
    } else at(index)
  }
  // The page this switch belongs to slides in, once it is on screen.
  if (entrance && Date.now() - entrance.at < 1500) {
    const dir = entrance.dir
    entrance = null
    let done = false
    const play = (): void => {
      const screen = seg.closest<HTMLElement>('#screen')
      if (done || !screen) return
      done = true
      screen.classList.remove('diary-view-in-next', 'diary-view-in-prev')
      void screen.offsetWidth
      screen.classList.add(dir > 0 ? 'diary-view-in-next' : 'diary-view-in-prev')
      setTimeout(() => screen.classList.remove('diary-view-in-next', 'diary-view-in-prev'), 320)
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(play)
    setTimeout(play, 60)
  }
  seg.append(thumb)
  for (const [label, view] of [['Day', 'day'], ['Week', 'week'], ['Month', 'month']] as const) {
    const b = el('button', { type: 'button', 'aria-pressed': String(current === view) }, label)
    b.addEventListener('click', () => {
      if (current === view) return
      tick()
      slide = { from: index, to: VIEWS.indexOf(view), at: Date.now() }
      entrance = { dir: VIEWS.indexOf(view) - index, at: Date.now() }
      for (const other of seg.querySelectorAll('button')) other.setAttribute('aria-pressed', String(other === b))
      at(VIEWS.indexOf(view))
      pick(view)
    })
    seg.append(b)
  }
  return seg
}

// The month's own visits as last drawn, after search, Who and Area: what the Diary's Pencilled tile counts on Month.
let shownInMonth: Clean[] = []
export function monthVisitsShown(): Clean[] { return shownInMonth }

function render(root: HTMLElement): void {
  endRowDrag()
  const p = payload
  if (!p) return
  const focusedVisit = root.querySelector<HTMLElement>('.diary-row-summary:focus')?.closest<HTMLElement>('[data-clean-id]')?.dataset.cleanId
  const keepY = window.scrollY
  beginDiaryColours(`month:${first}`, JSON.stringify([searchText, workerFilter, areaFilter]))
  clear(root)
  const head = heading()
  diaryHeading(head)
  diaryPage(root, head)
  root.append(head)
  const range = monthRange(first)
  const roster = p.round_days ?? []
  const colours = areaColours(customers ?? [])
  // A one-person round has no worker filter (Liam, 19 Oct 2026): one chosen before is cleared, so it never hides visits.
  const alone = worksAlone()
  if (alone) workerFilter = ''
  const shows = (cl: Clean): boolean => {
    const who = visitWorker(cl, roster).toLowerCase()
    const workerOk = !workerFilter || (workerFilter === '__unassigned' ? !who : who === workerFilter.toLowerCase())
    return workerOk && (!areaFilter || areaOfClean(cl) === areaFilter) && (!searchText || [cl.customer_name, cl.customer_address, visitJobLabel(cl)].join(' ').toLowerCase().includes(searchText.toLowerCase()))
  }
  // The Diary's one order for the calendar's chips too, as the chosen day lists them, Roundly's suggestions last (Astra's
  // final review, 5, 20 Oct 2026).
  const inOrder = (visits: Clean[]): Clean[] => [...diaryDayOrder(visits.filter((c) => !isSuggestion(c)), severalJobsOn()), ...orderDay(visits.filter(isSuggestion), isSuggestion)]
  const days = monthDays(first, p.cleans, range).map((d) => ({ ...d, visits: inOrder(d.visits.filter(shows)) }))
  shownInMonth = days.filter((d) => d.inMonth).flatMap((d) => d.visits)
  if (picked && !days.some((d) => d.date === picked)) picked = null

  // ---- the top line ----
  // One bar for Day, Week and Month (Liam, 9 Oct 2026: "this should be consistent at least on mobile and especially day
  // week month"; on a computer: "when switching from day, week, to month the month view changes these on the layout for
  // no good reason"): Diary, the magnifier, ⋯ and + Book a visit (tidy-up item 4, 20 Oct 2026, Liam's drawing
  // top-after.jpg), with "‹ October ›" and Day · Week · Month. The magnifier holds the search and Who; the areas are
  // the chips under the figures. ⋯ jumps to a date, or back to this month.
  const [y, mo] = first.split('-').map(Number) as [number, number]
  const toMonth = (n: -1 | 1): void => { monthStep = n; first = shiftMonth(first, n); picked = null; expandedDays.clear(); void load() }
  const step = (label: string, glyph: string, n: -1 | 1, extra = ''): HTMLElement => {
    const b = el('button', { type: 'button', class: `week-step${extra}`, 'aria-label': label, title: label }, extra ? el('span', { 'aria-hidden': 'true' }, glyph) : glyph)
    b.addEventListener('click', () => toMonth(n))
    return b
  }
  const toThisMonth = (): void => { first = monthOf(todayISO()); picked = null; expandedDays.clear(); void load() }
  const workers = [...new Set([...(p.workers ?? []), ...roster.map((r) => r.worker ?? '').filter(Boolean)])].sort()
  const worker = el('select', { 'aria-label': phone() ? 'Filter by worker' : 'Who: filter by worker' })
  worker.append(el('option', { value: '' }, 'All workers'), el('option', { value: '__unassigned' }, 'Unassigned'))
  for (const w of workers) worker.append(el('option', { value: w }, w))
  worker.value = workerFilter
  worker.addEventListener('change', () => { workerFilter = worker.value; render(root) })
  const search = el('input', { type: 'search', 'aria-label': 'Search diary', ...(phone() ? { placeholder: 'Search name, address or job…' } : { id: 'diary-desk-search', placeholder: 'Name, address or job' }) })
  search.value = searchText
  search.addEventListener('input', () => {
    searchText = search.value; hooks.setSearch?.(searchText)
    const cursor = search.selectionStart
    render(root)
    const fresh = root.querySelector<HTMLInputElement>('[aria-label="Search diary"]')
    fresh?.focus({preventScroll:true}); try { fresh?.setSelectionRange(cursor,cursor) } catch { /* search input selection */ }
  })
  const date = el('input', { type: 'date', 'aria-label': 'Jump to a date' }) as HTMLInputElement
  date.value = picked ?? todayISO()
  date.addEventListener('change', () => { if (!date.value || !date.validity.valid) return; first = monthOf(date.value); picked = date.value; selectionEntry = true; expandedDays.clear(); void load() })
  const more = diaryMoreMenu('More: dates', [{ title: 'Jump to a date…', hint: 'Or back to this month', fill: (box, close) => {
    const back = el('button', { type: 'button', class: 'diary-more-today' }, 'This month')
    back.addEventListener('click', () => { close(); toThisMonth() })
    box.replaceChildren(el('label', { class: 'diary-more-field' }, el('span', {}, 'Jump to a date'), date), ...(first !== monthOf(todayISO()) ? [back] : []))
    date.focus({ preventScroll: true })
  } },
  // A phone finds a visit from ⋯, as in Day and Week (phone 4: the magnifier went); with a search or Who in use the box
  // stays open, so this takes you to it.
  ...(phone() ? [{ title: 'Find a visit…', hint: 'By name, address, job or who', fill: (_box: HTMLElement, close: () => void) => {
    close(); optionsOpen = searchText || workerFilter ? true : !optionsOpen; render(root)
    root.querySelector<HTMLElement>(optionsOpen ? '[aria-label="Search diary"]' : '.diary-phone-views .diary-more > summary')?.focus({ preventScroll: true })
  } }] : [])])
  const findOpen = (phone() ? optionsOpen : deskFindOpen) || !!searchText || !!workerFilter
  const find = el('button', {type:'button', class:`diary-find-toggle${phone() ? ' diary-icon-button' : ''}${findOpen ? ' is-on' : ''}`, 'aria-expanded':String(findOpen), 'aria-controls':'diary-desk-find', 'aria-label':'Search and filters', title:'Search and filters'}, magnifier())
  find.addEventListener('click', () => {
    if (phone()) optionsOpen = !findOpen; else deskFindOpen = !findOpen
    render(root)
    root.querySelector<HTMLElement>(findOpen ? '.diary-find-toggle' : '[aria-label="Search diary"]')?.focus({preventScroll:true})
  })
  const book = el('button', {type:'button',class:'btn-primary'}, '+ Book a visit')
  book.addEventListener('click', () => hooks.book?.(picked ?? todayISO()))
  // Day or Week opens on the day picked, else on the month on show when it is not this one (Astra's final check).
  const views = threeViewSwitch('month', (view) => {
    if (view === 'month') return
    const shown = first === monthOf(todayISO()) ? todayISO() : first
    hooks.toMode?.(view, picked ?? (phone() ? undefined : shown))
  })
  let findBar: HTMLElement
  let monthSub: HTMLElement | null = null
  if (phone()) {
    // Month C uses Week's top row, real tiles, then its date card in the same places.
    void find
    monthSub = el('p', {class:'diary-phone-summary-text'}, `${MONTHS[mo - 1]} ${y}`)
    const nav = el('div', {class:'week-nav'}, step('Previous month', '‹', -1), el('div', {class:'week-heading'}, el('h3', {}, monthHeading(first, todayISO())), monthSub), step('Next month', '›', 1))
    head.classList.add('diary-phone-head', 'is-merged')
    head.replaceChildren()
    root.append(el('div', {class:'diary-phone-dates diary-phone-views'}, views, book, more))
    if (hooks.phoneTiles) root.append(hooks.phoneTiles(p, customers))
    root.append(el('div', {class:'diary-strip-card diary-month-card'}, nav))
    findBar = el('div', {class:'diary-phone-find', id:'diary-desk-find', role:'search', 'aria-label':'Find visits'}, search, ...(alone ? [] : [el('div', {class:'diary-phone-find-row'}, worker)]))
  } else {
    // Week's computer controls, including Today; the month's date range and figures sit under its name.
    const nav = el('div', {class:'week-nav diary-desk-nav', role:'group', 'aria-label':'Change dates'}, step('Previous month', '‹', -1, ' diary-desk-step'), step('Next month', '›', 1, ' diary-desk-step'))
    const thisMonth = el('button', { type: 'button', class: 'diary-desk-today' }, 'Today')
    thisMonth.addEventListener('click', toThisMonth)
    views.classList.add('diary-desk-views')
    head.className = 'page-heading diary-desk-head diary-top'
    head.replaceChildren(el('div', {class:'diary-desk-title'}, el('h2', {}, monthHeading(first, todayISO())), el('p', {class:'diary-period diary-desk-sub', 'aria-live':'polite'}, `${MONTHS[mo - 1]} ${y}`)), find, nav, thisMonth, more, views, book)
    findBar = el('div', {class:'diary-desk-filters', id:'diary-desk-find', role:'search', 'aria-label':'Find visits'},
      el('label', {class:'diary-field diary-field-grow'}, el('span', {}, 'Search visits'), search),
      ...(alone ? [] : [el('div', {class:'diary-field'}, el('span', {'aria-hidden':'true'}, 'Who'), worker)]))
  }
  findBar.hidden = !findOpen
  root.append(findBar)

  // ---- the month's money ----
  const inMonth = days.filter((d) => d.inMonth).flatMap((d) => d.visits)
  const booked = dayMoney(inMonth)
  const matching = !!(searchText || workerFilter || areaFilter)
  const done = inMonth.filter((cl) => cl.state === 'done').reduce((s, cl) => s + (cl.amount_pence ?? 0), 0)
  const sign = currencySymbol()
  const real = inMonth.filter(c => c.state === 'booked' && chipKind(c) !== 'suggested')
  const value = (rows: Clean[]): string => `${shortMoney(rows.reduce((n,c) => n + (c.amount_pence ?? 0), 0), sign)}${rows.some(c => c.amount_pence == null) ? ' + unpriced' : ''}`
  if (phone()) {
    // The month's figures on the line under its name, as Week's are (phone 4); each part kept whole on a narrow phone.
    const doneN = inMonth.filter((cl) => cl.state === 'done').length
    const parts = [monthDates(first, todayISO()), `${shortMoney(booked.pence, sign)}${booked.noPrice ? '+' : ''}${matching ? ' matching' : ''}`, `${doneN} of ${booked.jobs} done`]
    monthSub?.replaceChildren(parts.map(part => part.replace(/ /g, '\u00a0')).join(' · '))
    void stat; void countLine
  } else {
    // Design B on a computer (Liam, 24 Oct 2026, https://claude.ai/artifact/GvAv9orrjczy2hBG43Dq6E): the month's figures
    // are tiles, with Due first and Needs you last (the Diary fills those two in: hooks.deskFrame).
    const figure = (key: string, drawing: Drawing, count: string, words: string, tone = ''): HTMLElement =>
      el('div', { class: 'diary-wtile', 'data-tile': key }, el('span', { class: `diary-wtile-ring${tone ? ` is-${tone}` : ''}`, 'aria-hidden': 'true' }, lineDrawing(drawing, 17)), el('strong', { class: 'diary-wtile-count' }, count), el('span', { class: 'diary-wtile-words' }, words))
    const agreed = real.filter(c => c.confirmed), pencilled = real.filter(c => !c.confirmed)
    const sub = root.querySelector<HTMLElement>('.diary-desk-sub')
    const doneN = inMonth.filter(c => c.state === 'done').length
    if (sub) sub.textContent = `${monthDates(first, todayISO())} · ${booked.jobs} ${matching ? 'matching ' : ''}visit${booked.jobs === 1 ? '' : 's'} · ${shortMoney(booked.pence, sign)}${booked.noPrice ? ' + unpriced' : ''} · ${doneN} of ${booked.jobs} done`
    // The redesign (Month 4): Due, Pencilled and Needs you first (the Diary fills them: hooks.deskFrame), then the
    // month's money. "Pencilled" now names the jobs still to agree (src/pencilled.ts), so the money not agreed yet is
    // "not agreed" (Astra's review, 26 Oct 2026: one word, one meaning).
    root.append(el('div', { class: 'diary-week-tiles diary-month-tiles diary-tile-row', role: 'group', 'aria-label': 'This month at a glance' },
      el('div', { class: 'diary-wtile-slot', 'data-slot': 'due' }),
      el('span', { class: 'diary-tile-sep', 'aria-hidden': 'true' }),
      figure('agreed', 'calendarTick', value(agreed), 'agreed', 'green'),
      figure('pencilled', 'pencil', value(pencilled), 'not agreed', 'dashed'),
      figure('done', 'tick', shortMoney(done, sign), 'done', 'green')))
    // What the count line said beyond the tiles: Roundly's suggestions and the month's estimated time.
    const c = diaryCounts(inMonth)
    const more = [c.suggested ? `${c.suggested} suggested by Roundly` : '', c.agreed || c.pencilled ? diaryEstimateLine(c.minutes, c.missing, c.agreed + c.pencilled) : ''].filter(Boolean)
    if (more.length) root.append(el('p', { class: 'diary-month-tiles-note' }, more.join(' · ')))
  }

  // ---- areas and key ----
  // Only this month's own visits, not the neighbouring days the grid shows or a cancelled visit (Liam, 27 Oct 2026: "Some
  // of these pills shouldnt appear if they arent on this month? Ie i dont think mayfair is on my dropby round").
  const areasHere = [...new Set(p.cleans.filter((cl) => cl.date.startsWith(first.slice(0, 7)) && cl.state !== 'cancelled').map(areaOfClean))]
    .sort((a, b) => (colours.get(a) ?? 99) - (colours.get(b) ?? 99) || (a === 'Not set' ? 1 : b === 'Not set' ? -1 : a.localeCompare(b)))
  // A chosen area stays listed even in a month without it, with "All areas" to clear it (Astra, 30 Sep).
  if (areaFilter && !areasHere.includes(areaFilter)) areasHere.unshift(areaFilter)
  const filters = el('div', { class: 'month-filters' })
  // Keep even a single real area's rename control beside the key.
  if (areasHere.some(a => a !== 'Not set') || areaFilter) {
    filters.append(el('span', { class: 'month-filters-label' }, 'Areas'))
    for (const a of areasHere) {
      const on = areaFilter === a
      // A tap opens the area: rename it, or Show only it (Liam's pick A, 27 Oct 2026,
      // https://claude.ai/artifact/1zfWhmDP4dh9gYqwKyavpb). The chosen one, and "Not set", still just filter.
      const renames = !on && a !== 'Not set'
      const b = el('button', { type: 'button', class: `month-area${areaFilter && !on ? ' month-area-dim' : ''}`, 'aria-pressed': String(on), title: on ? 'Show every area' : renames ? `Rename ${a}, or show only it` : `Show only ${a}` })
      b.append(el('i', { class: `area-dot ${areaClass(a, colours)}` }), a)
      b.addEventListener('click', () => {
        if (renames) { void openAreaRename(a, { showOnly: () => { areaFilter = a; render(root) } }); return }
        areaFilter = on ? '' : a; render(root)
      })
      filters.append(b)
    }
    if (areaFilter) {
      const all = el('button', { type: 'button', class: 'month-area' }, 'All areas')
      all.addEventListener('click', () => { areaFilter = ''; render(root) })
      filters.append(all)
    }
  }
  // The same four-item key as Week. Suggestions are explained only when this calendar contains one,
  // including its neighbouring days, independent of filters hiding individual names.
  filters.prepend(diaryColourKey(monthDays(first, p.cleans, range).some(d => d.visits.some(isSuggestion))))
  if (searchText || workerFilter || areaFilter) {
    const names = [searchText ? `Search: ${searchText}` : '',workerFilter ? `Worker: ${workerFilter === '__unassigned' ? 'Unassigned' : workerFilter}` : '',areaFilter ? `Area: ${areaFilter}` : ''].filter(Boolean)
    const reset = el('button', {type:'button',class:'diary-clear-filters'}, 'Clear filters')
    reset.addEventListener('pointerdown', event => { if (hooks.isEditing?.()) event.preventDefault() })
    reset.addEventListener('mousedown', event => { if (hooks.isEditing?.()) event.preventDefault() })
    reset.addEventListener('click', () => {
      if (dragging || rowDrag || movesInFlight.size || hooks.isEditing?.() || document.querySelector('dialog[open]')) { toast('Finish editing or moving this visit before clearing filters.'); return }
      searchText = ''; hooks.setSearch?.(''); workerFilter = ''; areaFilter = ''
      // On a phone the search folds away with the filters, as Week's does (it stayed open over the month).
      if (phone()) optionsOpen = false
      render(root)
      root.querySelector<HTMLElement>(phone() ? '.diary-phone-views .diary-more > summary' : '[aria-label="Search diary"]')?.focus({preventScroll:true})
    })
    filters.prepend(el('span', {class:'diary-active-filters'},names.join(' · ')),reset)
  }
  root.append(filters)

  // ---- the grid ----
  const cal = el('div', { class: 'month-grid', role: 'grid', 'aria-label': `${MONTHS[mo - 1]} ${y}` })
  for (const d of ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']) cal.append(el('div', { class: 'month-dow', role: 'columnheader' }, d))
  cal.append(el('div', { class: 'month-dow month-week-head', role: 'columnheader' }, 'Week'))
  const today = todayISO()
  let week: Clean[] = []
  days.forEach((day, i) => {
    // No names under the day numbers in a one-person round (Liam, 19 Oct 2026).
    cal.append(dayCell(day, today, colours, sign, root, alone ? '' : roster.find((r) => r.date === day.date)?.worker ?? ''))
    week.push(...day.visits)
    if (i % 7 === 6) {
      const w = dayMoney(week)
      cal.append(el('div', { class: 'month-week' }, el('b', {}, `${w.jobs} job${w.jobs === 1 ? '' : 's'}`), splitMoney(week, sign)))
      week = []
    }
  })
  root.append(cal)
  if (monthStep) {
    diaryMotion(cal, [{opacity:.65,transform:`translateX(${monthStep > 0 ? 12 : -12}px)`},{opacity:1,transform:'translateX(0)'}],180)
    monthStep = 0
  }
  const list = picked ? days.find((d) => d.date === picked) : null
  if (list) root.append(dayList(list, colours, sign, root))
  if (!phone()) hooks.deskFrame?.(root, p, customers)
  finishDiary(root)
  if (focusedVisit) root.querySelector<HTMLButtonElement>(`[data-clean-id="${focusedVisit}"] .diary-row-summary`)?.focus({preventScroll:true})
  if (orderLanding) {
    diaryOrderSettle(root, orderLanding); orderLanding = null
  }
  if (landing && payload?.cleans.some(c => c.id === landing!.id && c.date === landing!.date)) {
    diaryMoveSettle(root,landing.move); landing = null
  }
  window.scrollTo({ top: keepY })
  if (selectionEntry) {
    selectionEntry = false
    diarySelectionTint(root.querySelector<HTMLElement>(`.month-day[data-date="${picked}"]`))
    const list = root.querySelector<HTMLElement>('.month-daylist')
    list?.querySelector<HTMLElement>('#month-selection-title')?.focus({preventScroll:true})
    list?.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'})
    diaryMotion(list, [{opacity:0},{opacity:1}],160)
  }
}

function splitMoney(rows: Clean[], sign: string): HTMLElement {
  const box = el('div', {class:'diary-month-money'})
  // "not agreed", as the month's own tile says (the redesign: Pencilled is the list of visits still to agree).
  for (const [label, agreed] of [['agreed', true], ['not agreed', false]] as const) {
    const visits = rows.filter(c => c.state === 'booked' && !isSuggestion(c) && !!c.confirmed === agreed)
    if (visits.length) box.append(el('span', {}, `${shortMoney(visits.reduce((n,c) => n + (c.amount_pence ?? 0), 0), sign)}${visits.some(c => c.amount_pence == null) ? '+' : ''} ${label}`))
  }
  return box
}

// "3 · £60", "3 · £60+" when some have no price, "1 · no price" when none has one (Astra, 30 Sep).
function sumText(m: { jobs: number; pence: number; noPrice: number }, sign: string): string {
  if (m.noPrice && m.pence === 0) return `${m.jobs} · no price`
  return `${m.jobs} · ${shortMoney(m.pence, sign)}${m.noPrice ? '+' : ''}`
}

function stat(value: string, label: string): HTMLElement {
  return el('div', { class: 'month-stat' }, el('b', {}, value), el('span', {}, label))
}

function areaClass(area: string, colours: Map<string, number>): string {
  const i = colours.get(area)
  return i === undefined ? 'area-none' : `area-${i + 1}`
}

// `who`: the day's worker from the roster (the diary's "Nobody on / Sendi"), including a day a worker booked in
// My days (0040). Shown under the day's number so the owner sees the month's schedule at a glance.
function dayCell(day: MonthDay, today: string, colours: Map<string, number>, sign: string, root: HTMLElement, who = ''): HTMLElement {
  const dow = (new Date(`${day.date}T12:00:00Z`).getUTCDay() + 6) % 7
  const cell = el('div', {
    class: `month-day${day.inMonth ? '' : ' month-day-out'}${day.date === today ? ' month-today' : ''}${day.date < today ? ' month-past' : ''}${dow >= 5 ? ' month-weekend' : ''}${picked === day.date ? ' month-picked' : ''}`,
    role: 'gridcell',
    tabindex: '0',
    'data-date': day.date,
  })
  const m = dayMoney(day.visits)
  cell.setAttribute('aria-label', `${dayLabel(day.date)}${who ? `, ${who} on` : ''}: ${m.jobs} ${searchText || workerFilter || areaFilter ? 'matching ' : ''}job${m.jobs === 1 ? '' : 's'}, ${shortMoney(m.pence, sign)}`)
  const num = el('button', { type: 'button', class: 'month-num', title: 'Show this day' }, String(Number(day.date.slice(8))))
  num.addEventListener('click', (e) => { e.stopPropagation(); pick(day.date, root) })
  const head = el('div', { class: 'month-day-head' }, num)
  // The existing date head retains jobs/money; one shared whole-day mark replaces its progress circle.
  const wholeDay = (payload?.cleans ?? []).filter(c => c.date === day.date)
  const badge = el('span', {class:'diary-month-state'})
  badge.append(colourDay(badge, wholeDay, `month:${day.date}`, false, {date:day.date, closed:closedDiaryDay(payload, day.date)}))
  head.append(badge)
  if (!phone() && m.jobs) head.append(el('span', { class: 'month-day-sum' }, sumText(m, sign)))
  cell.classList.toggle('diary-is-today', day.date === today)
  cell.append(head)
  if (who) cell.append(el('span', { class: 'month-day-who', title: `${who} is on` }, who))
  if (day.visits.length) {
    const counts = diaryCounts(day.visits)
    cell.append(el('span', {class:'diary-month-workload'}, [`${m.jobs} ${searchText || workerFilter || areaFilter ? 'matching ' : ''}job${m.jobs === 1 ? '' : 's'}`, counts.minutes ? `${Math.floor(counts.minutes / 60)}h ${counts.minutes % 60}m open` : '', counts.missing ? `${counts.missing} no estimate` : ''].filter(Boolean).join(' · ')))
  }
  const limit = 3
  const expanded = expandedDays.has(day.date)
  for (const cl of day.visits.slice(0, limit)) cell.append(chip(cl, colours, sign, root))
  if (day.visits.length > limit) {
    const more = el('button', { type: 'button', class: 'month-more', 'aria-expanded': String(expanded) }, expanded ? 'Show fewer' : phone() ? `+${day.visits.length - limit}` : `+${day.visits.length - limit} more`)
    const extra = el('div', {class:'diary-month-extra'})
    for (const cl of day.visits.slice(limit)) extra.append(chip(cl,colours,sign,root))
    extra.hidden = !expanded; extra.inert = !expanded
    more.addEventListener('click', (e) => {
      e.stopPropagation()
      const pointer = (e as PointerEvent).pointerType
      if ((pointer ? pointer !== 'mouse' : touch()) && !expandedDays.has(day.date)) { pick(day.date, root); return }
      const open = more.getAttribute('aria-expanded') !== 'true'
      if (open) expandedDays.add(day.date); else expandedDays.delete(day.date)
      more.setAttribute('aria-expanded',String(open))
      more.textContent = open ? 'Show fewer' : phone() ? `+${day.visits.length-limit}` : `+${day.visits.length-limit} more`
      diaryPanel(extra,open,180); more.focus({preventScroll:true})
    })
    cell.append(more, extra)
  }
  if (m.jobs) cell.append(splitMoney(day.visits, sign))
  cell.addEventListener('click', () => pick(day.date, root))
  cell.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target === cell) pick(day.date, root) })
  // A job dropped here moves to this day.
  cell.addEventListener('dragover', (e) => { if (dragging) { e.preventDefault(); cell.classList.add('month-over') } })
  cell.addEventListener('dragleave', () => cell.classList.remove('month-over'))
  cell.addEventListener('drop', (e) => {
    e.preventDefault()
    cell.classList.remove('month-over')
    const id = Number(e.dataTransfer?.getData('text/x-roundly-visit'))
    const cl = payload?.cleans.find((c) => c.id === id)
    if (cl) moveTo(cl, day.date, root)
  })
  return cell
}

function chip(cl: Clean, colours: Map<string, number>, sign: string, root: HTMLElement): HTMLElement {
  const kind = chipKind(cl)
  // Week's state marks and colours, with completion separate from the saved payment mark.
  // Day agreement sets the edge and mark; time and customer warnings retain their own words.
  const look = visitColour(cl) ?? (kind === 'suggested' ? 'suggested' : '')
  const c = el('button', { type: 'button', class: `month-chip ${areaClass(areaOfClean(cl), colours)} month-chip-${kind}`, 'data-clean-id':String(cl.id), 'data-look':look })
  if (!phone() && (look === 'done' || look === 'agreed' || look === 'pencilled')) c.append(stateMark(look))
  // A customer with more than one job (several jobs): "Mrs Smith · Gutters".
  c.append(el('span', { class: 'month-chip-name' }, hasExtraJobs(cl.customer_id) ? `${cl.customer_name ?? 'Visit'} · ${visitJobLabel(cl)}` : cl.customer_name ?? 'Visit'))
  if (kind === 'done_paid') c.append(el('b', { class: 'month-chip-mark key-paid', 'aria-hidden':'true' }, '✓'))
  if (kind === 'done_unpaid') c.append(el('b', { class: 'month-chip-mark key-owed', 'aria-hidden':'true' }, sign))
  if (cl.amount_pence !== null && cl.amount_pence !== undefined) c.append(el('span', { class: 'month-chip-price' }, shortMoney(cl.amount_pence, sign)))
  // The words the Diary and Messages use for this visit (visit-words.ts).
  const visit = visitWords(cl, diaryCustomer(cl, hooks.customerOf?.(cl.customer_id)))
  const words = visit.text
  if (visit.time === 'time to confirm' || visit.need === 'check') { c.classList.add('diary-chip-time-warning'); c.append(el('span', {class:'diary-state-warning', role:'img', 'aria-label':words})) }
  c.title = `${cl.customer_name ?? 'Visit'}, ${words}${canMove(cl) ? '. Drag to another day, or tap to move.' : ''}`
  c.setAttribute('aria-label', `${c.querySelector('.month-chip-name')?.textContent ?? 'Visit'}, ${words}${canMove(cl) ? '. Drag to another day, or tap to move.' : ''}`)
  attachJobDrag(c, cl, root)
  c.addEventListener('click', (e) => {
    e.stopPropagation()
    movingOpen = canMove(cl) ? cl.id : null
    pick(cl.date, root)
  })
  return c
}

function pick(date: string, root: HTMLElement): void {
  if (picked !== date) movingOpen = movingOpen !== null && payload?.cleans.find((c) => c.id === movingOpen)?.date === date ? movingOpen : null
  picked = date
  selectionEntry = true
  render(root)
}

function dayList(day: MonthDay, colours: Map<string, number>, sign: string, root: HTMLElement): HTMLElement {
  const m = dayMoney(day.visits)
  const box = el('section', { class: 'month-daylist', 'aria-label': dayLabel(day.date) })
  const open = el('button', { type: 'button', class: 'link-button' }, 'Open this day')
  open.addEventListener('click', () => hooks.toWeek(day.date))
  // "· Sendi on", never in a one-person round (Liam, 19 Oct 2026).
  const who = worksAlone() ? undefined : payload?.round_days?.find((r) => r.date === day.date)?.worker
  box.append(el('div', { class: 'month-daylist-head' }, el('h3', {id:'month-selection-title',tabindex:'-1'}, `${dayLabel(day.date)}${who ? ` · ${who} on` : ''} · ${m.jobs} job${m.jobs === 1 ? '' : 's'} · ${m.pence === 0 && m.noPrice ? 'no price' : `${shortMoney(m.pence, sign)}${m.noPrice ? ` + ${m.noPrice} with no price` : ''}`}`), open))
  const back = el('button', { type: 'button', class: 'link-button' }, 'Back to calendar')
  back.addEventListener('click', () => { picked = null; render(root); root.querySelector<HTMLElement>(`.month-day[data-date="${day.date}"]`)?.focus() })
  box.querySelector('.month-daylist-head')?.append(back)
  box.append(countLine(day.visits))
  const card = el('section', {class:'day-card', 'data-date':day.date})
  const extras = payload ? hooks.dayExtras?.(day.date, day.visits, payload) : null
  {
    const title = box.querySelector('.month-daylist-head h3'); if (title) title.textContent = `Selected day · ${dayLabel(day.date)} · ${m.jobs} ${searchText || workerFilter || areaFilter ? 'matching jobs' : 'jobs'}`
    box.querySelector('.month-daylist-head')?.append(countLine(day.visits))
    box.querySelector(':scope > .diary-counts')?.remove()
    if (extras) card.append(extras.head)
    card.append(diaryDayTotals(day.visits)); box.append(card)
    if (searchText || workerFilter || areaFilter) card.append(el('p', {class:'diary-filter-note'}, 'Figures show matching visits. Day close-out covers the whole day.'))
  }
  let previousBand = ''
  if (!day.visits.length) card.append(el('p', { class: 'muted' }, payload && monthDays(first,payload.cleans).some(d => d.date === day.date && d.visits.length) ? 'No matching visits.' : 'Nothing booked.'))
  // The Diary's one order and headings (tidy-up item 7; GPT-6.1's review, E08, 20 Oct 2026): a time set first in time
  // order, agreed or not, then agreed with no time, then not yet agreed, then completed, as Day draws them (diary-stops.ts);
  // Roundly's suggestions last, as Day has them.
  const groups = diaryDayGroups(day.visits.filter((c) => !isSuggestion(c)), severalJobsOn())
  const bands: [Clean[], string][] = [
    [groups.timed, 'Times set'],
    [groups.agreed, groups.agreed.some((c) => !c.confirmed) ? 'Agreed visits & linked jobs' : 'Agreed visits'],
    [groups.pencilled, 'Pencilled visits'],
    [groups.completed, 'Completed'],
    [orderDay(day.visits.filter(isSuggestion), isSuggestion), 'Suggestions'],
  ]
  for (const { cl, band } of bands.flatMap(([rows, band]) => rows.map((cl) => ({ cl, band })))) {
    if (band !== previousBand) { card.append(el('h4', {class:'day-group'}, band)); previousBand = band }
    const kind = chipKind(cl)
    const words = visitWords(cl, diaryCustomer(cl, hooks.customerOf?.(cl.customer_id))).text
    const row = el('div', { class: `month-row ${areaClass(areaOfClean(cl), colours)} month-chip-${kind}`, 'data-clean-id': String(cl.id) })
    row.append(el('span', { class: 'month-row-who' }, cl.customer_name ?? 'Visit', words ? el('span', { class: 'month-row-state' }, ` · ${words}`) : ''))
    row.append(el('span', { class: 'month-row-amt' }, cl.amount_pence !== null && cl.amount_pence !== undefined ? shortMoney(cl.amount_pence, sign) : 'no price'))
    if (payload && hooks.renderVisit) hooks.renderVisit(cl, row, payload, !!workerFilter || !!areaFilter || !!searchText)
    card.append(row)
    attachJobDrag(row, cl, root)
    if (canMove(cl)) {
      const mv = el('button', { type: 'button', class: 'month-move' }, 'Move')
      ;(row.querySelector('.diary-quick-actions') ?? row).append(mv)
      const picker = el('div', { class: 'month-picker' })
      const input = el('input', { type: 'date', id: `month-move-${cl.id}`, min: todayISO(), 'aria-label': `New day for ${cl.customer_name ?? 'this visit'}` }) as HTMLInputElement
      input.value = moveDrafts.get(cl.id) ?? cl.date
      input.addEventListener('input', () => { if (input.value) moveDrafts.set(cl.id, input.value) })
      const go = el('button', { type: 'button', class: 'btn-primary' }, 'Move here')
      go.addEventListener('click', () => { if (input.value && input.validity.valid) moveTo(cl, input.value, root) })
      picker.append(el('label', { for: `month-move-${cl.id}` }, 'Move to'), input, go)
      picker.hidden = movingOpen !== cl.id
      mv.addEventListener('click', () => { movingOpen = movingOpen === cl.id ? null : cl.id; picker.hidden = movingOpen !== cl.id; if (!picker.hidden) input.focus() })
      card.append(picker)
    }
  }
  if (extras) card.append(extras.footer)
  return box
}

// The week diary's hold-or-scroll gesture, on both the chips and the full day's rows. Mouse dragging keeps
// the same HTML drop path. A clipped, fixed copy follows a finger without widening the page or the grid.
function attachJobDrag(row: HTMLElement, cl: Clean, root: HTMLElement): void {
  if (!canMove(cl)) return
  row.classList.add('month-can-grab')
  row.draggable = true
  let onControl = false
  row.addEventListener('dragstart', (e) => {
    if (!row.draggable || onControl || rowDrag || movesInFlight.has(cl.id)) { e.preventDefault(); return }
    dragging = true
    e.dataTransfer?.setData('text/x-roundly-visit', String(cl.id))
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
    row.classList.add('month-chip-dragging')
  })
  row.addEventListener('dragend', () => {
    dragging = false
    row.classList.remove('month-chip-dragging')
    clearDropMarks(root)
    refreshAfterDrag()
  })
  row.addEventListener('pointerdown', (e) => {
    row.draggable = e.pointerType === 'mouse'
    const control = (e.target as Element | null)?.closest('button, a, input, select, textarea, summary')
    onControl = !control?.matches('.diary-row-summary') && pressOnControl(control === row ? null : control?.tagName ?? null, false)
    if (e.pointerType === 'mouse' || e.button !== 0 || !e.isPrimary || onControl) return
    if (rowDrag?.phase === 'held') return
    endRowDrag()
    if (movesInFlight.has(cl.id)) return
    rowDrag = {
      row, root, cleanId: cl.id, gen: routeGen, pointerId: e.pointerId, pointerType: e.pointerType,
      startX: e.clientX, startY: e.clientY, lastX: e.clientX, lastY: e.clientY, startedAt: performance.now(),
      phase: 'press', timer: null, frame: null, layer: null, preview: null, fingerDx: 0, fingerDy: 0,
    }
    window.addEventListener('pointermove', rowDragMove)
    window.addEventListener('pointerup', rowDragUp)
    window.addEventListener('pointercancel', rowDragCancel)
    window.addEventListener('keydown', rowDragKey)
    window.addEventListener('blur', rowDragCancel)
    rowDrag.timer = window.setTimeout(startRowGrab, LONG_PRESS_MS)
  })
  row.addEventListener('touchmove', (e) => {
    if (rowDrag?.row === row && rowDrag.phase === 'held') e.preventDefault()
  }, { passive: false })
  row.addEventListener('contextmenu', (e) => { if (rowDrag?.row === row) e.preventDefault() })
}

function dayUnderPointer(): HTMLElement | null {
  if (!rowDrag) return null
  const day = document.elementFromPoint(rowDrag.lastX, rowDrag.lastY)?.closest<HTMLElement>('.month-day[data-date]')
  return day && rowDrag.root.contains(day) ? day : null
}

function clearDropMarks(root: HTMLElement): void {
  for (const d of root.querySelectorAll('.month-over')) d.classList.remove('month-over')
}

function rowDragFrame(): void {
  if (!rowDrag || rowDrag.phase !== 'held') return
  if (!active || rowDrag.gen !== routeGen || !rowDrag.row.isConnected) { endRowDrag(); return }
  const { preview, root, lastX, lastY, fingerDx, fingerDy } = rowDrag
  if (preview) preview.style.transform = `translate(${lastX - fingerDx}px, ${lastY - fingerDy}px) scale(1.02)`
  clearDropMarks(root)
  dayUnderPointer()?.classList.add('month-over')
  if (lastY < AUTO_SCROLL_EDGE_PX) window.scrollBy(0, -autoScrollSpeed(lastY))
  else if (window.innerHeight - lastY < AUTO_SCROLL_EDGE_PX) window.scrollBy(0, autoScrollSpeed(window.innerHeight - lastY))
  rowDrag.frame = requestAnimationFrame(rowDragFrame)
}

function startRowGrab(): void {
  if (!rowDrag || rowDrag.phase === 'held') return
  const cl = payload?.cleans.find((c) => c.id === rowDrag!.cleanId)
  if (!active || rowDrag.gen !== routeGen || !rowDrag.row.isConnected || !cl || !canMove(cl) || movesInFlight.has(cl.id)) { endRowDrag(); return }
  const { row, pointerId, root } = rowDrag
  rowDrag.phase = 'held'
  dragging = true
  if (rowDrag.timer !== null) { clearTimeout(rowDrag.timer); rowDrag.timer = null }
  const rect = row.getBoundingClientRect()
  rowDrag.fingerDx = rowDrag.lastX - rect.left
  rowDrag.fingerDy = rowDrag.lastY - rect.top
  const preview = row.cloneNode(true) as HTMLElement
  preview.classList.add('month-drag-preview')
  preview.style.width = `${rect.width}px`
  const layer = el('div', { class: 'month-drag-layer', 'aria-hidden': 'true', inert: '' }, preview)
  root.append(layer)
  rowDrag.preview = preview
  rowDrag.layer = layer
  row.classList.add('month-chip-dragging')
  try { row.setPointerCapture(pointerId) } catch { /* the window listeners still see the pointer */ }
  navigator.vibrate?.(10)
  rowDragFrame()
}

function refreshAfterDrag(): void {
  if (!reloadAfterDrag) return
  reloadAfterDrag = false
  if (active) void load()
}

function endRowDrag(): void {
  if (!rowDrag) return
  const { row, root, timer, frame, layer, pointerId, phase } = rowDrag
  rowDrag = null
  if (timer !== null) clearTimeout(timer)
  if (frame !== null) cancelAnimationFrame(frame)
  window.removeEventListener('pointermove', rowDragMove)
  window.removeEventListener('pointerup', rowDragUp)
  window.removeEventListener('pointercancel', rowDragCancel)
  window.removeEventListener('keydown', rowDragKey)
  window.removeEventListener('blur', rowDragCancel)
  layer?.remove()
  row.classList.remove('month-chip-dragging')
  clearDropMarks(root)
  try { if (row.hasPointerCapture(pointerId)) row.releasePointerCapture(pointerId) } catch { /* already released */ }
  dragging = false
  if (phase === 'held') swallowNextClick()
  refreshAfterDrag()
}

function rowDragMove(e: PointerEvent): void {
  if (!rowDrag || e.pointerId !== rowDrag.pointerId) return
  rowDrag.lastX = e.clientX
  rowDrag.lastY = e.clientY
  if (rowDrag.phase === 'held') return
  const verdict = shouldGrab(rowDrag.pointerType, e.clientX - rowDrag.startX, e.clientY - rowDrag.startY, performance.now() - rowDrag.startedAt)
  if (verdict === 'abandon') endRowDrag()
  else if (verdict === 'grab') startRowGrab()
}

function rowDragUp(e: PointerEvent): void {
  if (!rowDrag || e.pointerId !== rowDrag.pointerId) return
  if (rowDrag.phase !== 'held') { endRowDrag(); return }
  rowDrag.lastX = e.clientX
  rowDrag.lastY = e.clientY
  const date = dayUnderPointer()?.dataset.date
  const { root, gen, cleanId } = rowDrag
  const cl = payload?.cleans.find((c) => c.id === cleanId)
  endRowDrag()
  if (active && gen === routeGen && date && cl) moveTo(cl, date, root)
}

function rowDragCancel(): void { endRowDrag() }

function rowDragKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') { e.preventDefault(); endRowDrag() }
}

function swallowNextClick(): void {
  const swallow = (e: MouseEvent): void => { e.preventDefault(); e.stopPropagation() }
  window.addEventListener('click', swallow, { capture: true })
  setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)
}

// One PATCH, the same one the week diary makes: the server stamps the visit as a person's choice and, as it
// always has, a moved visit is not agreed until the customer says so. Undo puts back the date AND whether it
// was agreed; what else a move changes (Roundly's own suggestion becoming a person's choice) stays (Astra).
function moveTo(cl: Clean, date: string, root: HTMLElement): void {
  if (!canMove(cl) || cl.date === date) return
  if (movesInFlight.has(cl.id)) { toast(`Still moving ${firstName(cl)}. Try again in a few seconds.`); return }
  if (date < todayISO()) { toast(GONE_DAY_WORDS); return }
  const was = { date: cl.date, version: cl.version, confirmed: cl.confirmed ? 1 : 0, timeAgreed: timeIsAgreed(cl) ? cl.time_agreed_for : null }
  const missed = hooks.missedLink?.(cl) ?? null
  const gen = routeGen, who = diaryPreferenceKey('move')
  const effect = diaryMoveCapture(cl.date,date,cl.id)
  let reverse: DiaryMoveEffect | undefined
  // Still this screen, still the month: only then is the month drawn again; otherwise the screen now showing is
  // told something changed (Astra, 30 Sep).
  const here = (): boolean => routeGen === gen && active && who === diaryPreferenceKey('move')
  const refresh = (): void => { if (here()) void load(); else window.dispatchEvent(new CustomEvent('dropby:data-changed')) }
  picked = date
  movingOpen = null
  movesInFlight.add(cl.id)
  moveDrafts.delete(cl.id)
  void mutate({
    label: `Move: ${cl.customer_name ?? ''} to ${dayLabel(date)}`,
    method: 'PATCH',
    path: `/api/cleans/${cl.id}`,
    body: { version: was.version, date },
    onConflict: () => { if (here()) void load() },
  }).then((ack) => {
    // Released on the answer, as the week diary does: a move kept on the phone for signal waits in the save
    // queue, which sends a second move of the same visit only after the first.
    movesInFlight.delete(cl.id)
    if (ack.ok) {
      if (here() && !ack.noop) landing = { id: cl.id, date, reverse: false, move:effect }
      const row = ack.row as Partial<Clean> | undefined
      if (row && payload) {
        const held = payload.cleans.find((c) => c.id === cl.id)
        if (held) Object.assign(held, row)
      }
      if (here()) render(root)
      refresh()
    } else if (here() && ack.status >= 400 && ack.status < 500 && ack.status !== 409) {
      toast(ack.error ? `Couldn't move ${firstName(cl)}: ${ack.error}` : `Couldn't move ${firstName(cl)}.`)
    }
    if (!here() || ack.noop) return
    undoPatch({
      text: ack.ok ? `Moved ${firstName(cl)} to ${dayLabel(date)}${nowWhose(cl, ack.row)}${was.confirmed ? ' · New date needs agreement' : ''}` : 'Move not confirmed · Check save status',
      ...(ack.ok && missed ? { link: missed(date, (ack.row as { version?: number } | undefined)?.version ?? was.version + 1, { date: was.date, confirmed: was.confirmed, time_agreed_for: was.timeAgreed }) }
        : ack.ok && was.confirmed && hooks.messageThem ? { link: hooks.messageThem(cl) } : {}),
      label: `Undo move: ${cl.customer_name ?? ''} back to ${dayLabel(was.date)}`,
      path: `/api/cleans/${cl.id}`,
      ack,
      version: was.version,
      body: { date: was.date, confirmed: was.confirmed, time_agreed_for: was.timeAgreed, undo_restore: true },
      before: () => { landing = null; if (here()) reverse = diaryMoveCapture(date,was.date,cl.id) },
      onAck: back => { if (back.ok && !back.noop && here() && reverse) { landing = {id:cl.id,date:was.date,reverse:true,move:reverse}; picked = was.date } },
      after: refresh,
    })
  })
}
